import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { PrimaryGrid } from '@ui/components/common/Grid';
import { Box, Button, Chip, IconButton, Tooltip, useMediaQuery, useTheme, Typography, CircularProgress, alpha } from '@mui/material';
import { Trash, Export, AddCircle, Archive, Send2, Flash } from 'iconsax-react';
import { UnarchiveOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import { StyledMenu } from '@ui/components/common/Menu';
import OutlinedIconButton from '@ui/components/common/IconButton';
import { useAppSelector, useAppDispatch } from '@application/store/useStore';
import { fetchFamilyFestAttendees, getFamilyFestCompaniesAsync } from '@application/features/familyFest/familyFestAttendeesThunk';
import SearchFilter from '@ui/components/Button/SearchFilter';
import { hasPermission } from '@application/helpers/permission-helper';
import ConfirmationDialog from '@ui/components/common/Dialog/ConfirmationDialog';
import CustomDialog from '@ui/components/common/Dialog';
import { Iconly } from 'react-iconly';
import FamilyInfoDialog from './FamilyInfoDialog';
import SendWaitlistInviteDialog from './SendWaitlistInviteDialog';
import ConvertWaitlistSlotsDialog from './ConvertWaitlistSlotsDialog';
import RunAutoInviteJobDialog from './RunAutoInviteJobDialog';
import MoveWaitlistDialog from './MoveWaitlistDialog';
import EditFamilyResponseDialog from '../EditFamilyResponseDialog';
import { archiveFamilyFestAttendeeAsync, offboardFamilyFestAttendeeAsync, unarchiveFamilyFestAttendeeAsync, deleteFamilyFestAttendeeAsync, downloadFamilyFestAttendeesExcelAsync, getFamilyFestAttendeeDetailAsync, generateFamilyFestInviteLinkAsync, resendFamilyFestInvitationEmailAsync, sendFamilyFestReminderEmailAsync, getFamilyFestReminderEligibleAttendeesAsync } from '@application/features/familyFest/familyFestAttendeesThunk';
import { fetchFamilyFestWaitlist, fetchFamilyFestWaitlistInvitePreviewAsync, sendFamilyFestWaitlistInvitationsAsync, sendFamilyFestWaitlistLinksAsync, fetchFamilyFestCapacitySlotsAsync, convertFamilyFestWaitlistSlotsAsync, runFamilyFestWaitlistAutoInviteAsync, deleteFamilyFestWaitlistEntryAsync, moveFamilyFestWaitlistEntryAsync } from '@application/features/familyFest/familyFestWaitlistThunk';
import { InvitationStatusEnum } from '@application/enums/event-status-enum';
import { parseFamilyFestInvitationStatus } from '@application/features/familyFest/invitation-status';
import { FamilyFestAttendanceStatusEnum } from '@application/enums/family-fest-registration-enums';
import { EventTypeEnum } from '@application/enums/event-type-enum';
import { formatRegisteredDateOnly } from '@application/utilities/date-helper';
import { convertUtcToLocal } from '@application/utilities/date-time-formater';
function formatFamilyFestComingWithLabel(raw, t) {
    const v = String(raw ?? '').trim();
    if (!v)
        return '';
    const n = v.toLowerCase();
    if (n === 'guest' || n === 'introduction')
        return t('familyForm.comingWithGuest', 'Guest');
    if (n === 'family')
        return t('familyForm.comingWithFamily', 'Family');
    if (n === 'alone')
        return t('familyForm.comingWithAlone', 'Alone');
    return v;
}
function formatTravelModeLabel(raw, t) {
    const v = String(raw ?? '').trim();
    if (!v)
        return '';
    const n = v.toLowerCase();
    if (n === 'auto' || n === 'car')
        return t('familyFest.travelModeCar', 'Car');
    if (n === 'bus')
        return t('familyFest.travelModeBus', 'Bus');
    if (n === 'airplane' || n === 'plane')
        return t('familyFest.travelModePlane', 'Plane');
    if (n === 'arranged')
        return t('familyFest.travelModeArranged', 'Arranged');
    return v;
}
function formatAirportLabel(raw, t) {
    const v = String(raw ?? '').trim();
    if (!v)
        return '';
    const normalized = v.toLowerCase();
    const key = `Airport.${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
    return t(key, v);
}
const ACTIONS_COLUMN_WIDTH = 120;
const STATUS_COLUMN_WIDTH = 120;
const pinnedColumnSize = (width) => ({
    width: `${width}px !important`,
    minWidth: `${width}px !important`,
    maxWidth: `${width}px !important`,
    flex: `0 0 ${width}px !important`,
    boxSizing: 'border-box'
});
const getOpaqueColorMix = (baseColor, mixColor, mixRatio) => `color-mix(in srgb, ${mixColor} ${Math.round(mixRatio * 100)}%, ${baseColor})`;
const pinnedHeaderChildrenBg = (backgroundColor) => ({
    '& .MuiDataGrid-columnHeaderDraggableContainer, & .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContainerContent': {
        backgroundColor
    }
});
function calculateAge(dateOfBirth) {
    if (!dateOfBirth)
        return '';
    const dob = new Date(dateOfBirth);
    if (Number.isNaN(dob.getTime()))
        return '';
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const monthDiff = now.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
        age -= 1;
    }
    return age >= 0 ? String(age) : '';
}
const FamilyResponsesGrid = ({ eventId, onOpenAddDialog, onEditRow, isEditDialogLoading = false, selectedUserDataId, refetchAttendeesRef, onAttendeesChanged }) => {
    const dispatch = useAppDispatch();
    const { attendees, totalCount, loading, error } = useAppSelector((state) => state.familyFestAttendees);
    const selectedCompanyIds = useAppSelector((state) => state.company.selectedCompanyId);
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const loggedInUserPermissions = useAppSelector((state) => state.auth.currentUser?.permissions || []);
    const canEditEvent = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Update');
    const canDeleteEvent = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Delete');
    const currentUserRoles = useAppSelector((state) => state.auth.currentUser?.role);
    const isCollaborator = Array.isArray(currentUserRoles) &&
        (currentUserRoles.includes('Collaborator') || currentUserRoles.includes('TransportManager'));
    const isTransportManager = Array.isArray(currentUserRoles) && currentUserRoles.includes('TransportManager');
    const canResendInvitation = Array.isArray(currentUserRoles) &&
        (currentUserRoles.includes('Sfeermeester') || currentUserRoles.includes('HappinessManager'));
    const isHappinessManager = Array.isArray(currentUserRoles) && currentUserRoles.includes('HappinessManager');
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const [selectedTab, setSelectedTab] = useState('active');
    const [searchValue, setSearchValue] = useState('');
    const [gridFilterOpen, setGridFilterOpen] = useState(false);
    const [filters, setFilters] = useState({});
    const [resetFiltersKey, setResetFiltersKey] = useState(0);
    const [companies, setCompanies] = useState([]);
    // Waiting list tab: served by its own dedicated endpoint/thunk, kept out of the shared
    // familyFestAttendees redux slice since it's a separate concern from the other tabs.
    const [waitlistRows, setWaitlistRows] = useState([]);
    const [waitlistTotalCount, setWaitlistTotalCount] = useState(0);
    const [waitlistLoading, setWaitlistLoading] = useState(false);
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [inviteSending, setInviteSending] = useState(false);
    const [autoInviteRunning, setAutoInviteRunning] = useState(false);
    const [autoInviteConfirmOpen, setAutoInviteConfirmOpen] = useState(false);
    const [autoInviteError, setAutoInviteError] = useState(null);
    const [autoInvitePreview, setAutoInvitePreview] = useState(undefined);
    const [inviteError, setInviteError] = useState(null);
    // Capacity and the waiting list's expected persons come from the backend; the UI only displays them.
    const [invitePreview, setInvitePreview] = useState(null);
    // Waiting list: move free (released) waiting-list slots to regular capacity.
    const [convertDialogOpen, setConvertDialogOpen] = useState(false);
    const [convertSaving, setConvertSaving] = useState(false);
    const [convertError, setConvertError] = useState(null);
    const [capacitySlots, setCapacitySlots] = useState(null);
    // Waiting list: edit expected persons of a still-waitlisted entry.
    const [deleteWaitlistRow, setDeleteWaitlistRow] = useState(null);
    const [deleteWaitlistSaving, setDeleteWaitlistSaving] = useState(false);
    // Waiting list: row menu (move/delete) and the Move to position dialog.
    const [waitlistMenuAnchorEl, setWaitlistMenuAnchorEl] = useState(null);
    const [waitlistMenuRow, setWaitlistMenuRow] = useState(null);
    const [moveDialogRow, setMoveDialogRow] = useState(null);
    const [waitlistedCount, setWaitlistedCount] = useState(null);
    const [moveSaving, setMoveSaving] = useState(false);
    // Waiting list: edit a waitlisted registration in the full edit dialog.
    const [editRegistrationRow, setEditRegistrationRow] = useState(null);
    // Reminder dialog: own fetched list (status Invited/InProgress, reminder not yet sent) + own selection.
    const [reminderEligibleRows, setReminderEligibleRows] = useState([]);
    const [reminderEligibleTotalCount, setReminderEligibleTotalCount] = useState(0);
    const [reminderEligibleLoading, setReminderEligibleLoading] = useState(false);
    const [reminderPaginationModel, setReminderPaginationModel] = useState({
        page: 0,
        pageSize: 10
    });
    const [selectedReminderAttendeeIds, setSelectedReminderAttendeeIds] = useState([]);
    // Waiting-list rows selected for "Send invite email" (grid row ids, i.e. attendee ids).
    const [selectedWaitlistIds, setSelectedWaitlistIds] = useState([]);
    const [linksSending, setLinksSending] = useState(false);
    // Invitation ids awaiting confirmation before the registration email goes out (name set for a single row-menu send); null = dialog closed.
    const [confirmLinks, setConfirmLinks] = useState(null);
    useEffect(() => {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, [searchValue, filters, selectedTab]);
    // Reset filters when switching tabs so stale status options don't carry over
    useEffect(() => {
        setFilters({});
        setResetFiltersKey((k) => k + 1);
    }, [selectedTab]);
    useEffect(() => {
        dispatch(getFamilyFestCompaniesAsync())
            .unwrap()
            .then((companies) => setCompanies(companies))
            .catch(() => undefined);
    }, [dispatch]);
    // Grid column fields don't always match the backend's sort keys (e.g. firstName/lastName -> fullname).
    const SORT_FIELD_MAP = {
        firstName: 'fullname',
        lastName: 'fullname',
        middleName: 'middlename',
        email: 'email',
        totalPersonCount: 'totalpers',
        registrationDate: 'registrationdate',
        companyName: 'companyname',
        comingWith: 'comingwith',
        age: 'age',
        travelMode: 'travelmode',
        numOfCars: 'numofcars',
        invitationStatus: 'status'
    };
    const refetchAttendees = React.useCallback(() => {
        if (!eventId)
            return;
        const isArchiveTab = selectedTab === 'archive';
        const isNotAttendingTab = selectedTab === 'notAttending';
        const isWaitlistTab = selectedTab === 'waitlist';
        if (isWaitlistTab) {
            setWaitlistLoading(true);
            dispatch(fetchFamilyFestWaitlist({
                eventId,
                req: {
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    searchTerm: searchValue || undefined,
                    invitationStatusFilter: filters.status ?? undefined
                }
            }))
                .unwrap()
                .then((response) => {
                setWaitlistRows(response.items);
                setWaitlistTotalCount(response.totalCount);
                setWaitlistedCount(response.waitlistedCount);
            })
                .catch(() => undefined)
                .finally(() => setWaitlistLoading(false));
            return;
        }
        const sort = sortModel[0];
        dispatch(fetchFamilyFestAttendees({
            eventId,
            req: {
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                isArchived: isArchiveTab,
                isComingToEvent: isArchiveTab || isWaitlistTab ? undefined : isNotAttendingTab ? false : true,
                isWaitingListFilter: isWaitlistTab ? true : undefined,
                sortBy: sort ? (SORT_FIELD_MAP[sort.field] ?? sort.field) : 'createdon',
                sortOrder: sort?.sort ?? 'desc',
                searchTerm: searchValue || undefined,
                firstNameFilter: filters.firstName || undefined,
                lastNameFilter: filters.lastName || undefined,
                emailFilter: filters.email || undefined,
                companyIdFilter: filters.companyName || undefined,
                invitationStatusFilter: filters.invitationStatus ?? undefined,
                cameFromWaitlist: filters.cameFromWaitlist ?? undefined
            }
        }));
    }, [
        dispatch,
        eventId,
        selectedTab,
        searchValue,
        filters,
        paginationModel,
        sortModel,
        selectedCompanyIds
    ]);
    useEffect(() => {
        if (!refetchAttendeesRef)
            return;
        refetchAttendeesRef.current = refetchAttendees;
        return () => {
            refetchAttendeesRef.current = null;
        };
    }, [refetchAttendees, refetchAttendeesRef]);
    useEffect(() => {
        refetchAttendees();
    }, [refetchAttendees]);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [optionsMenuAnchorEl, setOptionsMenuAnchorEl] = useState(null);
    // Family info dialog
    const [familyInfoOpen, setFamilyInfoOpen] = useState(false);
    const [familyInfoPersons, setFamilyInfoPersons] = useState([]);
    // Confirmation dialogs
    const [archiveConfirmationOpen, setArchiveConfirmationOpen] = useState(false);
    const [offboardConfirmationOpen, setOffboardConfirmationOpen] = useState(false);
    const [offboardTargetName, setOffboardTargetName] = useState('');
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [actionTargetId, setActionTargetId] = useState(null);
    const [bulkArchiveConfirmationOpen, setBulkArchiveConfirmationOpen] = useState(false);
    const [bulkUnarchiveConfirmationOpen, setBulkUnarchiveConfirmationOpen] = useState(false);
    const [bulkDeleteConfirmationOpen, setBulkDeleteConfirmationOpen] = useState(false);
    const [selectedRowsForBulkOperation, setSelectedRowsForBulkOperation] = useState([]);
    // Send reminder email
    const [reminderConfirmationOpen, setReminderConfirmationOpen] = useState(false);
    const [sendingReminderEmail, setSendingReminderEmail] = useState(false);
    const openFamilyInfoForRow = async (rowId) => {
        try {
            const resp = await dispatch(getFamilyFestAttendeeDetailAsync(rowId)).unwrap();
            if (!resp.isSuccess || !resp.data) {
                throw new Error(resp.message || 'Could not load family info');
            }
            const detail = resp.data;
            // Accommodation is family-wide: once assigned (bus pickup or car), it covers the whole
            // family, so every person shows the same accommodation value.
            const accommodationRequired = String(detail.isAccommodationAssigned ?? false);
            const primary = {
                firstName: detail.firstName ?? '',
                middleName: detail.middleName ?? '',
                lastName: detail.lastName ?? '',
                relationshipToYou: '',
                DateOfBirth: detail.dateOfBirth ?? null,
                email: detail.email ?? '',
                Address: detail.addressLine ?? '',
                TravelMode: detail.travelMode ?? '',
                Option: String(detail.isWheelchairDependent),
                accommodationRequired
            };
            const members = (detail.members ?? []).map((member) => ({
                firstName: member.firstName ?? '',
                middleName: member.middleName ?? '',
                lastName: member.lastName ?? '',
                relationshipToYou: member.relationshipToPrimary ?? '',
                DateOfBirth: member.dateOfBirth ?? null,
                email: member.email ?? '',
                Address: member.addressLine ?? '',
                TravelMode: member.travelMode ?? '',
                Option: String(member.isWheelchairDependent),
                accommodationRequired
            }));
            const persons = [primary, ...members];
            if (persons.length > 1) {
                setFamilyInfoPersons(persons);
                setFamilyInfoOpen(true);
            }
            else {
                toast.info(t('familyForm.noFamilyMembers', 'No family members found.'));
            }
        }
        catch (error) {
            toast.error(error instanceof Error ? error.message : 'Could not load family info');
        }
    };
    // ── Reminder email preview grid columns ──
    const reminderPreviewColumns = React.useMemo(() => [
        {
            field: 'firstName',
            headerName: t('familyForm.firstName', 'First name') || 'First name',
            flex: 1,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => params.value || '-'
        },
        {
            field: 'lastName',
            headerName: t('familyForm.lastName', 'Last name') || 'Last name',
            flex: 1,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => params.value || '-'
        },
        {
            field: 'email',
            headerName: t('common.email', 'Email') || 'Email',
            flex: 1.2,
            minWidth: 180,
            sortable: false,
            renderCell: (params) => params.value || '-'
        },
        {
            field: 'invitationStatus',
            headerName: t('events.eventDetails.rsvpTrackingTab.status', 'Status') || 'Status',
            flex: 0.8,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => {
                const status = parseFamilyFestInvitationStatus(params.value);
                const label = status === InvitationStatusEnum.Invited
                    ? t('events.eventDetails.invitationsTab.invited')
                    : status === InvitationStatusEnum.InProgress
                        ? t('events.eventDetails.invitationsTab.inProgress')
                        : (params.value ?? '-');
                return (_jsx(Chip, { label: label, size: 'medium', color: status === InvitationStatusEnum.InProgress ? 'primary' : 'default', variant: 'soft' }));
            }
        }
    ], [t]);
    // ── Columns ──
    const createFamilyColumns = () => {
        const cellStyle = {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%'
        };
        const cols = [
            {
                field: 'firstName',
                type: 'string',
                headerName: t('familyForm.firstName', 'First name') || 'First name',
                minWidth: 140,
                flex: 0.8,
                renderCell: (params) => (_jsxs("span", { title: params.value ?? '', style: { ...cellStyle, display: 'inline-flex', alignItems: 'center', gap: 6 }, children: [params.row?.isWaitingList === true && (_jsx("span", { title: String(t('events.eventDetails.responsesTab.cameFromWaitlist', 'Came from the waiting list')), style: { display: 'inline-flex', flexShrink: 0 }, children: _jsx(Icon, { icon: 'mdi:timer-outline', width: 18, height: 18, color: theme.palette.text.secondary }) })), _jsx("span", { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }, children: params.value ?? '' })] }))
            },
            {
                field: 'middleName',
                headerName: t('familyForm.middleName', 'Middle name') || 'Middle name',
                minWidth: 140,
                flex: 0.8,
                renderCell: (params) => (_jsx("span", { title: params.value ?? '', style: cellStyle, children: params.value ?? '' }))
            },
            {
                field: 'lastName',
                type: 'string',
                headerName: t('familyForm.lastName', 'Last name') || 'Last name',
                minWidth: 140,
                flex: 0.8,
                renderCell: (params) => (_jsx("span", { title: params.value ?? '', style: cellStyle, children: params.value ?? '' }))
            },
            {
                field: 'email',
                type: 'string',
                headerName: t('common.email', 'Email') || 'Email',
                minWidth: 180,
                flex: 1.2,
                renderCell: (params) => (_jsx("span", { title: params.value ?? '', style: cellStyle, children: params.value ?? '' }))
            },
            {
                field: 'companyName',
                headerName: t('familyForm.company', 'Company') || 'Company',
                minWidth: 140,
                flex: 1,
                renderCell: (params) => (_jsx("span", { title: params.value ?? '', style: cellStyle, children: params.value ?? '' }))
            },
            {
                // Hidden (columnVisibilityModel): only here so the filter panel shows its Yes/No dropdown.
                field: 'cameFromWaitlist',
                headerName: t('events.eventDetails.responsesTab.cameFromWaitlist', 'Came from the waiting list') ||
                    'Came from the waiting list',
                sortable: false,
                valueGetter: (_value, row) => row.isWaitingList === true
            },
            {
                field: 'totalPersonCount',
                headerName: t('familyForm.numOfPersons', 'Num. of persons') || 'Persons',
                minWidth: 100,
                flex: 0.6,
                renderCell: (params) => _jsx("span", { style: cellStyle, children: params.value ?? '' })
            },
            {
                field: 'comingWith',
                headerName: t('familyForm.comingWith', 'Coming with') || 'Coming with',
                minWidth: 150,
                flex: 0.8,
                renderCell: (params) => {
                    const row = params.row;
                    const label = formatFamilyFestComingWithLabel(row.comingWith, t);
                    const isAlone = String(row.comingWith ?? '')
                        .trim()
                        .toLowerCase() === 'alone';
                    const isGuest = ['guest', 'introduction'].includes(String(row.comingWith ?? '')
                        .trim()
                        .toLowerCase());
                    const memberCount = Number(row.memberCount ?? 0);
                    return (_jsxs(Box, { display: 'flex', flexDirection: 'column', justifyContent: 'center', children: [_jsx(Typography, { variant: 'body2', color: 'text.primary', noWrap: true, title: label, children: label }), !isAlone && memberCount > 0 && (_jsx(Typography, { variant: 'caption', color: 'text.secondary', sx: {
                                    cursor: 'pointer',
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }, onClick: (e) => {
                                    e.stopPropagation();
                                    void openFamilyInfoForRow(row.id);
                                }, children: `${memberCount} ${isGuest ? t('events.eventDetails.transportTab.member') : t('events.eventDetails.transportTab.members')}` }))] }));
                }
            },
            {
                field: 'age',
                headerName: t('familyForm.age', 'Age') || 'Age',
                minWidth: 80,
                flex: 0.5,
                valueGetter: (_value, row) => calculateAge(row.dateOfBirth),
                renderCell: (params) => _jsx("span", { style: cellStyle, children: params.value ?? '' })
            },
            {
                field: 'travelMode',
                headerName: t('familyForm.travelMode', 'Travel mode') || 'Travel',
                minWidth: 140,
                flex: 1.2,
                renderCell: (params) => {
                    const row = params.row;
                    const label = formatTravelModeLabel(row.travelMode, t);
                    const isPlane = String(row.travelMode ?? '')
                        .trim()
                        .toLowerCase() === 'plane';
                    const airportLabel = isPlane ? formatAirportLabel(row.preferredAirport, t) : '';
                    return (_jsxs(Box, { display: 'flex', flexDirection: 'column', minWidth: 0, children: [_jsx("span", { title: label, style: cellStyle, children: label }), airportLabel && (_jsx(Typography, { variant: 'caption', color: 'text.secondary', noWrap: true, title: airportLabel, children: airportLabel }))] }));
                }
            },
            {
                field: 'numOfCars',
                headerName: t('familyForm.numOfCars', 'Num. of cars') || 'Num. of cars',
                minWidth: 120,
                flex: 0.6,
                renderCell: (params) => _jsx("span", { style: cellStyle, children: params.value ?? 0 })
            },
            {
                field: 'registrationDate',
                headerName: t('familyForm.registrationDate', 'Registration date') || 'Registration date',
                minWidth: 120,
                flex: 0.6,
                renderCell: (params) => (_jsx("span", { style: cellStyle, children: formatRegisteredDateOnly(params.value ?? 0) }))
            },
            {
                field: 'invitationStatus',
                headerName: t('events.eventDetails.rsvpTrackingTab.status', 'Status') || 'Status',
                width: STATUS_COLUMN_WIDTH,
                minWidth: STATUS_COLUMN_WIDTH,
                maxWidth: STATUS_COLUMN_WIDTH,
                renderCell: (params) => {
                    const raw = params.value;
                    const status = parseFamilyFestInvitationStatus(raw);
                    if (status == null) {
                        return (_jsx("span", { title: raw ?? '', style: cellStyle, children: raw ?? '' }));
                    }
                    let chipProps;
                    let sx = {};
                    switch (status) {
                        case InvitationStatusEnum.Invited:
                            chipProps = {
                                label: t('events.eventDetails.invitationsTab.invited'),
                                size: 'medium',
                                color: 'default',
                                variant: 'soft'
                            };
                            // sx = {
                            //   color: 'common.white',
                            //   '& .MuiChip-label': {
                            //     color: 'common.white'
                            //   }
                            // };
                            break;
                        case InvitationStatusEnum.InProgress:
                            chipProps = {
                                label: t('events.eventDetails.invitationsTab.inProgress'),
                                size: 'medium',
                                color: 'primary',
                                variant: 'soft'
                            };
                            break;
                        case InvitationStatusEnum.Registered:
                            chipProps = {
                                label: t('events.eventDetails.invitationsTab.registered'),
                                size: 'medium',
                                color: 'success',
                                variant: 'soft'
                            };
                            break;
                        case InvitationStatusEnum.NotComing:
                            chipProps = {
                                label: t('events.eventDetails.invitationsTab.notComing'),
                                size: 'medium',
                                color: 'default',
                                variant: 'soft'
                            };
                            break;
                        default:
                            chipProps = {
                                label: t('events.pending'),
                                size: 'medium',
                                color: 'default',
                                variant: 'soft'
                            };
                    }
                    return _jsx(Chip, { ...chipProps, sx: sx });
                }
            },
            {
                field: 'reason',
                headerName: t('familyForm.reason', 'Reason') || 'Reason',
                minWidth: 200,
                flex: 1.2,
                sortable: false,
                valueGetter: (_value, row) => {
                    const othersLabel = t('AttendanceStatus.Others', 'Others');
                    if (row.attendanceStatus === FamilyFestAttendanceStatusEnum.Others) {
                        return row.reasonForCancellation
                            ? `${othersLabel}: ${row.reasonForCancellation}`
                            : othersLabel;
                    }
                    if (row.attendanceStatus === FamilyFestAttendanceStatusEnum.CantMakeIt) {
                        return t('AttendanceStatus.CantMakeIt', "I can't make it");
                    }
                    if (row.attendanceStatus === FamilyFestAttendanceStatusEnum.NoInterest) {
                        return t('AttendanceStatus.NoInterest', 'No interest');
                    }
                    if (row.attendanceStatus === FamilyFestAttendanceStatusEnum.NoLongerEmployed) {
                        return t('AttendanceStatus.NoLongerEmployed', 'No longer employed');
                    }
                    return '';
                },
                renderCell: (params) => (_jsx("span", { title: params.value ?? '', style: cellStyle, children: params.value ?? '' }))
            }
        ];
        // Actions
        if (!isTransportManager) {
            cols.push({
                field: 'actions',
                headerName: t('labels.actions') || 'Actions',
                width: ACTIONS_COLUMN_WIDTH,
                minWidth: ACTIONS_COLUMN_WIDTH,
                maxWidth: ACTIONS_COLUMN_WIDTH,
                headerAlign: 'center',
                align: 'center',
                sortable: false,
                filterable: false,
                renderCell: (params) => (_jsxs(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        width: '100%',
                        gap: 1,
                        flexShrink: 0
                    }, children: [selectedTab !== 'archive' && (_jsx(OutlinedIconButton, { size: 'small', icon: isEditDialogLoading && selectedUserDataId === params.row.id ? (_jsx(CircularProgress, { size: 16, color: 'inherit' })) : (_jsx(Iconly, { name: 'Edit', set: 'curved', primaryColor: theme.palette.text.primary })), onClick: () => onEditRow?.(params.row.id), disabled: !canEditEvent || isCollaborator || isTransportManager || !onEditRow })), _jsx(OutlinedIconButton, { size: 'small', icon: _jsx(Icon, { icon: 'eva:more-horizontal-fill', color: theme.palette.text.primary }), onClick: (event) => handleMenuOpen(event, params.row.id), disabled: isCollaborator || isTransportManager })] }))
            });
        }
        if (selectedTab === 'waitlist') {
            const waitlistFields = new Set([
                'firstName',
                'middleName',
                'lastName',
                'email',
                'companyName'
            ]);
            const waitlistCols = [
                {
                    field: 'queueNumber',
                    headerName: t('events.eventDetails.responsesTab.waitlistPosition', 'Position') || 'Position',
                    width: 90,
                    minWidth: 90,
                    sortable: false,
                    // Invited/InProgress rows hold no Queue #.
                    renderCell: (params) => _jsx("span", { style: cellStyle, children: params.value ?? '-' })
                },
                ...cols
                    .filter((col) => waitlistFields.has(col.field))
                    // No type: the filter drawer would otherwise offer text filters the waitlist endpoint ignores.
                    .map((col) => ({ ...col, sortable: false, type: undefined }))
            ];
            waitlistCols.push({
                field: 'expectedNoOfPersons',
                headerName: t('familyForm.numOfPersons', 'Num. of persons') || 'Num. of persons',
                minWidth: 120,
                flex: 0.6,
                sortable: false,
                renderCell: (params) => _jsx("span", { style: cellStyle, children: params.value ?? '' })
            });
            waitlistCols.push({
                field: 'adultCount',
                headerName: t('events.eventDetails.responsesTab.waitlistAdults', 'Adults') || 'Adults',
                minWidth: 90,
                flex: 0.4,
                sortable: false,
                renderCell: (params) => _jsx("span", { style: cellStyle, children: params.value ?? 0 })
            });
            waitlistCols.push({
                field: 'kidsCount',
                headerName: t('events.eventDetails.responsesTab.waitlistKids', 'Kids') || 'Kids',
                minWidth: 90,
                flex: 0.4,
                sortable: false,
                renderCell: (params) => _jsx("span", { style: cellStyle, children: params.value ?? 0 })
            });
            waitlistCols.push({
                field: 'travelMode',
                headerName: t('familyForm.travelMode', 'Travel mode') || 'Travel',
                minWidth: 140,
                flex: 0.8,
                sortable: false,
                renderCell: (params) => (_jsx("span", { style: cellStyle, children: formatTravelModeLabel(params.value, t) || '-' }))
            });
            waitlistCols.push({
                field: 'createdDate',
                headerName: t('events.eventDetails.responsesTab.waitlistCreatedDate', 'Created date') ||
                    'Created date',
                minWidth: 120,
                flex: 0.6,
                sortable: false,
                renderCell: (params) => (_jsx("span", { style: cellStyle, children: formatRegisteredDateOnly(params.value) }))
            });
            // Last time the link was sent (the completion button and Send invite both restart it); "-" if never sent.
            waitlistCols.push({
                field: 'waitingLinkSentTime',
                headerName: t('events.eventDetails.responsesTab.waitlistLinkSent', 'Link sent') || 'Link sent',
                minWidth: 150,
                flex: 0.7,
                sortable: false,
                renderCell: (params) => (_jsx("span", { style: cellStyle, children: params.row?.isWaitingLinkSent === true && params.value
                        ? convertUtcToLocal(params.value)
                        : '-' }))
            });
            waitlistCols.push({
                field: 'status',
                headerName: t('events.eventDetails.rsvpTrackingTab.status', 'Status') || 'Status',
                // Fixed width: the column is pinned (sticky) like the attendee grid's status column.
                width: STATUS_COLUMN_WIDTH,
                minWidth: STATUS_COLUMN_WIDTH,
                maxWidth: STATUS_COLUMN_WIDTH,
                sortable: false,
                renderCell: (params) => {
                    // Registered people are not listed here (they move to the attendee grid), so a row is
                    // still waiting, invited, or in progress (started registering within the 48h window).
                    const raw = String(params.row.status ?? '');
                    const isInProgress = ['InProgress', '2'].includes(raw);
                    const isInvited = ['Invited', '4'].includes(raw);
                    return (_jsx(Chip, { label: isInProgress
                            ? t('events.eventDetails.invitationsTab.inProgress', 'In progress')
                            : isInvited
                                ? t('events.eventDetails.responsesTab.waitlistStatusInvited', 'Invited')
                                : t('events.eventDetails.responsesTab.waitlistStatusWaiting', 'Waiting'), size: 'medium', color: isInProgress ? 'primary' : isInvited ? 'info' : 'default', variant: 'soft' }));
                }
            });
            waitlistCols.push({
                field: 'details',
                headerName: t('events.eventDetails.responsesTab.waitlistDetails', 'Details') || 'Details',
                minWidth: 180,
                flex: 1,
                sortable: false,
                renderCell: (params) => {
                    // What the waiting person still has to do: finish the form before a seat can be given.
                    if (params.row?.isRegistrationComplete === true) {
                        return (_jsx(Chip, { label: t('events.eventDetails.responsesTab.waitlistDetailsFormComplete', 'Form complete'), size: 'small', color: 'success', variant: 'soft' }));
                    }
                    return (_jsx(Chip, { label: t('events.eventDetails.responsesTab.waitlistDetailsFormMissing', 'Incomplete form'), size: 'small', color: 'warning', variant: 'soft' }));
                }
            });
            waitlistCols.push({
                field: 'actions',
                // Same header and fixed width as the main grid's Actions column: the pinned (sticky) "actions"
                // styling assumes ACTIONS_COLUMN_WIDTH, so a narrower column overlapped the cells next to it.
                headerName: t('labels.actions') || 'Actions',
                width: ACTIONS_COLUMN_WIDTH,
                minWidth: ACTIONS_COLUMN_WIDTH,
                maxWidth: ACTIONS_COLUMN_WIDTH,
                headerAlign: 'center',
                align: 'center',
                sortable: false,
                filterable: false,
                renderCell: (params) => {
                    // Only people holding an active invitation have a link to copy; only still-waiting
                    // people can be edited, moved or deleted (those live in the row menu).
                    const raw = String(params.row.status ?? '');
                    const hasActiveLink = ['Invited', '4', 'InProgress', '2'].includes(raw);
                    const isWaitlisted = ['Waitlisted', '8'].includes(raw);
                    if (isWaitlisted) {
                        // Same buttons as the attendee grid's Actions column.
                        return (_jsxs(Box, { sx: {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                width: '100%',
                                gap: 1,
                                flexShrink: 0
                            }, onMouseDown: (e) => e.stopPropagation(), children: [_jsx(OutlinedIconButton, { size: 'small', icon: _jsx(Iconly, { name: 'Edit', set: 'curved', primaryColor: theme.palette.text.primary }), onClick: (e) => {
                                        e?.stopPropagation();
                                        handleEditWaitlistRegistration(params.row.id);
                                    } }), _jsx(OutlinedIconButton, { size: 'small', icon: _jsx(Icon, { icon: 'eva:more-horizontal-fill', color: theme.palette.text.primary }), disabled: isCollaborator, onClick: (e) => {
                                        if (!e)
                                            return;
                                        e.stopPropagation();
                                        setWaitlistMenuAnchorEl(e.currentTarget);
                                        setWaitlistMenuRow(params.row);
                                    } })] }));
                    }
                    if (!hasActiveLink)
                        return _jsx("span", { style: cellStyle, children: "-" });
                    return (_jsx(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            width: '100%'
                        }, 
                        // Keep the grid's cell focus handling from swallowing the first click.
                        onMouseDown: (e) => e.stopPropagation(), children: _jsx(Tooltip, { title: t('actions.copyLink', 'Copy link'), children: _jsx(IconButton, { size: 'small', onClick: (e) => {
                                    e.stopPropagation();
                                    void handleCopyLink(params.row.id);
                                }, children: _jsx(Icon, { icon: 'eva:link-2-outline', color: theme.palette.text.primary }) }) }) }));
                }
            });
            return waitlistCols;
        }
        if (selectedTab === 'notAttending') {
            const hiddenForNotAttending = new Set([
                'comingWith',
                'age',
                'totalPersonCount',
                'numOfCars',
                'travelMode',
                'invitationStatus'
            ]);
            return cols.filter((col) => !hiddenForNotAttending.has(col.field));
        }
        if (selectedTab === 'archive') {
            return cols.filter((col) => col.field !== 'reason' && col.field !== 'invitationStatus');
        }
        // active tab
        return cols.filter((col) => col.field !== 'reason');
        return cols;
    };
    // ── Waiting list: send invitations ──
    const handleOpenInviteDialog = () => {
        if (!eventId)
            return;
        setInviteError(null);
        setInvitePreview(null);
        dispatch(fetchFamilyFestWaitlistInvitePreviewAsync(eventId))
            .unwrap()
            .then((preview) => setInvitePreview(preview))
            .catch(() => undefined);
        setInviteDialogOpen(true);
    };
    const handleCloseInviteDialog = () => {
        if (inviteSending)
            return;
        setInviteDialogOpen(false);
        setInviteError(null);
    };
    const handleSendWaitlistInvites = (slots) => {
        if (!eventId || !Number.isInteger(slots) || slots < 1)
            return;
        setInviteSending(true);
        setInviteError(null);
        dispatch(sendFamilyFestWaitlistInvitationsAsync({ eventId, slots }))
            .unwrap()
            .then(({ invited, message }) => {
            // Backend decides who is invited and returns "X invited, Y not invited" (localized).
            toast.success(message ||
                t('events.eventDetails.responsesTab.waitlistInvitesSent', {
                    invited,
                    notInvited: 0,
                    defaultValue: '{{invited}} invited, {{notInvited}} not invited.'
                }));
            setInviteDialogOpen(false);
            refetchAttendees();
        })
            .catch((error) => {
            // Backend validation (e.g. not enough capacity) is authoritative; show its message as-is.
            setInviteError(error?.error ||
                t('events.eventDetails.responsesTab.waitlistInviteFailed', 'Failed to send invitations.'));
        })
            .finally(() => setInviteSending(false));
    };
    // Sends the magic-link email to the given waiting-list invitations (unfinished people only; the backend skips the rest).
    const handleSendWaitlistLinks = (invitationIds) => {
        if (!eventId || invitationIds.length === 0)
            return;
        setLinksSending(true);
        dispatch(sendFamilyFestWaitlistLinksAsync({ eventId, invitationIds }))
            .unwrap()
            .then(({ sent, message }) => {
            toast.success(message ||
                t('events.eventDetails.responsesTab.waitlistLinksSent', {
                    count: sent,
                    defaultValue: '{{count}} invite email(s) sent.'
                }));
            setSelectedWaitlistIds([]);
            refetchAttendees();
        })
            .catch((error) => {
            toast.error(error?.error ||
                t('events.eventDetails.responsesTab.waitlistLinksFailed', 'Failed to send invite emails.'));
        })
            .finally(() => {
            setLinksSending(false);
            setConfirmLinks(null);
        });
    };
    // ── Waiting list: run the auto-invite job now ──
    const handleOpenAutoInviteConfirm = () => {
        if (!eventId)
            return;
        setAutoInvitePreview(undefined);
        setAutoInviteError(null);
        dispatch(fetchFamilyFestWaitlistInvitePreviewAsync(eventId))
            .unwrap()
            .then((preview) => setAutoInvitePreview(preview))
            .catch(() => setAutoInvitePreview(null));
        setAutoInviteConfirmOpen(true);
    };
    const handleRunAutoInvite = () => {
        if (autoInviteRunning)
            return;
        setAutoInviteRunning(true);
        setAutoInviteError(null);
        dispatch(runFamilyFestWaitlistAutoInviteAsync())
            .unwrap()
            .then((invited) => {
            // 0 is not an error: no free slots, nobody waiting, or the next person needs more slots (see JobRunAudits).
            if (invited > 0) {
                toast.success(t('events.eventDetails.responsesTab.waitlistAutoInviteDone', {
                    count: invited,
                    defaultValue: '{{count}} invited.'
                }));
            }
            else {
                toast.info(t('events.eventDetails.responsesTab.waitlistAutoInviteNone', 'Nobody invited: no free slots, or the next person does not fit.'));
            }
            setAutoInviteConfirmOpen(false);
            refetchAttendees();
        })
            .catch((error) => {
            setAutoInviteError(error?.error ||
                t('events.eventDetails.responsesTab.waitlistAutoInviteFailed', 'The auto-invite job failed.'));
        })
            .finally(() => setAutoInviteRunning(false));
    };
    // ── Waiting list: move free slots to regular capacity ──
    const handleOpenConvertDialog = () => {
        if (!eventId)
            return;
        setConvertError(null);
        setCapacitySlots(null);
        dispatch(fetchFamilyFestCapacitySlotsAsync(eventId))
            .unwrap()
            .then((slots) => setCapacitySlots(slots))
            .catch((error) => setConvertError(error?.error ||
            t('events.eventDetails.responsesTab.waitlistConvertFailed', 'Failed to move the slots.')));
        setConvertDialogOpen(true);
    };
    const handleCloseConvertDialog = () => {
        if (convertSaving)
            return;
        setConvertDialogOpen(false);
        setConvertError(null);
    };
    const handleConvertWaitlistSlots = (count) => {
        if (!eventId || !Number.isInteger(count) || count < 1)
            return;
        setConvertSaving(true);
        setConvertError(null);
        dispatch(convertFamilyFestWaitlistSlotsAsync({ eventId, count }))
            .unwrap()
            .then(({ converted, message }) => {
            toast.success(message ||
                t('events.eventDetails.responsesTab.waitlistSlotsConverted', {
                    count: converted,
                    defaultValue: '{{count}} slot(s) moved to regular capacity.'
                }));
            setConvertDialogOpen(false);
        })
            .catch((error) => {
            // Backend is authoritative (e.g. slots were invited meanwhile); show its message as-is.
            setConvertError(error?.error ||
                t('events.eventDetails.responsesTab.waitlistConvertFailed', 'Failed to move the slots.'));
        })
            .finally(() => setConvertSaving(false));
    };
    const handleEditWaitlistRegistration = (attendeeId) => {
        const row = waitlistRows.find((r) => r.id === attendeeId);
        if (!row)
            return;
        setEditRegistrationRow(row);
    };
    const handleConfirmDeleteWaitlistEntry = () => {
        if (!deleteWaitlistRow)
            return;
        setDeleteWaitlistSaving(true);
        dispatch(deleteFamilyFestWaitlistEntryAsync(deleteWaitlistRow.invitationId))
            .unwrap()
            .then((message) => {
            toast.success(message ||
                t('events.eventDetails.responsesTab.waitlistEntryDeleted', 'Removed from the waiting list.'));
            refetchAttendees();
        })
            .catch((error) => {
            toast.error(error?.error ||
                t('events.eventDetails.responsesTab.waitlistEntryDeleteFailed', 'Failed to remove from the waiting list.'));
        })
            .finally(() => {
            setDeleteWaitlistSaving(false);
            setDeleteWaitlistRow(null);
        });
    };
    const closeWaitlistMenu = () => {
        setWaitlistMenuAnchorEl(null);
        setWaitlistMenuRow(null);
    };
    // Places the row at Queue # target. Success and 409 both refetch.
    const handleMoveWaitlist = (row, target) => {
        if (!eventId)
            return;
        setMoveSaving(true);
        dispatch(moveFamilyFestWaitlistEntryAsync({
            eventId,
            request: { target, invitationId: row.invitationId }
        }))
            .unwrap()
            .then((message) => {
            toast.success(message ||
                t('events.eventDetails.responsesTab.waitlistMoved', 'Waiting list position updated.'));
        })
            .catch((error) => {
            toast.error(error?.error ||
                t('events.eventDetails.responsesTab.waitlistMoveFailed', 'Failed to move.'));
        })
            .finally(() => {
            setMoveSaving(false);
            setMoveDialogRow(null);
            refetchAttendees();
        });
    };
    // waitlistedCount (the bottom Queue #) comes with every waiting-list page.
    // The auto-invite job sets Invited: the person already has a seat and an invitation, so no new email is sent.
    const isWaitlistRowInvited = (row) => ['Invited', '4'].includes(String(row.status ?? ''));
    const isAtBottom = (row) => row.queueNumber == null || waitlistedCount == null || row.queueNumber >= waitlistedCount;
    // Figma "Overlay / Row menu" items, plus Delete (moved here from the row).
    const waitlistRowMenuActions = waitlistMenuRow
        ? [
            {
                text: t('actions.copyLink', 'Copy link'),
                icon: _jsx(Icon, { icon: 'eva:link-2-outline', width: 20, height: 20 }),
                to: () => handleCopyLink(waitlistMenuRow.id),
                // Only while the link is live: an expired row has no link to copy.
                disabled: waitlistMenuRow.isWaitingLinkSent !== true || waitlistMenuRow.isWaitingLinkExpired === true,
                group: true
            },
            {
                text: t('events.eventDetails.responsesTab.waitlistSendLinks', 'Send registration email'),
                icon: _jsx(Send2, { variant: 'Outline', size: 20 }),
                to: () => setConfirmLinks({
                    ids: [waitlistMenuRow.invitationId],
                    name: [waitlistMenuRow.firstName, waitlistMenuRow.lastName].filter(Boolean).join(' ')
                }),
                disabled: waitlistMenuRow.isRegistrationComplete === true ||
                    isWaitlistRowInvited(waitlistMenuRow) ||
                    linksSending ||
                    isCollaborator ||
                    !canEditEvent,
                group: true
            },
            {
                text: t('events.eventDetails.responsesTab.waitlistMoveToPosition', 'Move to position'),
                icon: _jsx(Icon, { icon: 'hugeicons:arrow-up-down', width: 20, height: 20 }),
                to: () => setMoveDialogRow(waitlistMenuRow),
                disabled: waitlistMenuRow.queueNumber == null,
                group: true
            },
            {
                text: t('events.eventDetails.responsesTab.waitlistMoveToTop', 'Move to top'),
                icon: _jsx(Icon, { icon: 'hugeicons:arrow-up-to-line', width: 20, height: 20 }),
                to: () => handleMoveWaitlist(waitlistMenuRow, 1),
                disabled: waitlistMenuRow.queueNumber == null || waitlistMenuRow.queueNumber === 1,
                group: true
            },
            {
                text: t('events.eventDetails.responsesTab.waitlistMoveToBottom', 'Move to bottom'),
                icon: _jsx(Icon, { icon: 'hugeicons:arrow-down-to-line', width: 20, height: 20 }),
                to: () => waitlistedCount != null && handleMoveWaitlist(waitlistMenuRow, waitlistedCount),
                disabled: isAtBottom(waitlistMenuRow),
                group: true
            },
            {
                text: t('actions.delete', 'Delete'),
                icon: _jsx(Trash, { variant: 'Outline', style: { width: 20, height: 20 } }),
                color: theme.palette.error.light,
                to: () => setDeleteWaitlistRow(waitlistMenuRow),
                disabled: !canDeleteEvent || isCollaborator,
                group: true
            }
        ]
        : [];
    // ── Rows ──
    const createFamilyRows = () => {
        if (selectedTab === 'waitlist')
            return waitlistRows;
        return attendees;
    };
    // ── Menu & actions ──
    const handleMenuOpen = (event, rowId) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setSelectedRowId(rowId);
    };
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setSelectedRowId(null);
    };
    const handleOptionsMenuOpen = (e) => setOptionsMenuAnchorEl(e.currentTarget);
    const handleOptionsMenuClose = () => setOptionsMenuAnchorEl(null);
    const handleDelete = (id) => {
        if (id) {
            setActionTargetId(id);
            setDeleteConfirmationOpen(true);
        }
    };
    const handleRowArchive = (id) => {
        if (id) {
            setActionTargetId(id);
            setArchiveConfirmationOpen(true);
        }
        handleMenuClose();
    };
    const handleRowUnarchive = (id) => {
        if (id) {
            setActionTargetId(id);
            setArchiveConfirmationOpen(true);
        }
        handleMenuClose();
    };
    const handleConfirmArchive = async () => {
        if (!actionTargetId)
            return;
        try {
            const data = await dispatch(archiveFamilyFestAttendeeAsync({ attendeeId: actionTargetId })).unwrap();
            if (!data.isSuccess)
                throw new Error(data.message || 'Request failed');
            toast.success(t('events.eventDetails.responsesTab.responseArchivedSuccessfully') ||
                'Response archived successfully');
            refetchAttendees();
            onAttendeesChanged?.();
        }
        catch (e) {
            toast.error(e instanceof Error ? e.message : 'Failed to archive');
        }
        setArchiveConfirmationOpen(false);
        setActionTargetId(null);
    };
    const handleRowOffboard = (id) => {
        if (id) {
            const row = attendees.find((r) => r.id === id);
            setOffboardTargetName(`${row?.firstName ?? ''} ${row?.lastName ?? ''}`.trim());
            setActionTargetId(id);
            setOffboardConfirmationOpen(true);
        }
        handleMenuClose();
    };
    const handleConfirmOffboard = async () => {
        if (!actionTargetId)
            return;
        try {
            const data = await dispatch(offboardFamilyFestAttendeeAsync(actionTargetId)).unwrap();
            if (!data.isSuccess)
                throw new Error(data.message || 'Request failed');
            toast.success(t('events.eventDetails.responsesTab.responseOffboardedSuccessfully') ||
                'Attendee offboarded successfully');
            refetchAttendees();
            onAttendeesChanged?.();
        }
        catch (e) {
            toast.error(e instanceof Error
                ? e.message
                : t('events.eventDetails.responsesTab.failedToOffboardResponse') ||
                    'Failed to offboard attendee.');
        }
        setOffboardConfirmationOpen(false);
        setActionTargetId(null);
        setOffboardTargetName('');
    };
    const handleConfirmUnArchive = async () => {
        if (!actionTargetId)
            return;
        try {
            const data = await dispatch(unarchiveFamilyFestAttendeeAsync(actionTargetId)).unwrap();
            if (!data.isSuccess)
                throw new Error(data.message || 'Request failed');
            toast.success(t('events.eventDetails.responsesTab.responseUnarchivedSuccessfully') ||
                'Response unarchived successfully');
            refetchAttendees();
        }
        catch (e) {
            toast.error(e instanceof Error
                ? e.message
                : t('events.eventDetails.responsesTab.failedToUnarchiveResponse') ||
                    'Failed to unarchive response. Please try again.');
        }
        setArchiveConfirmationOpen(false);
        setActionTargetId(null);
    };
    const handleConfirmDelete = async () => {
        if (!actionTargetId)
            return;
        try {
            const data = await dispatch(deleteFamilyFestAttendeeAsync({ attendeeId: actionTargetId })).unwrap();
            if (!data.isSuccess)
                throw new Error(data.message || 'Request failed');
            toast.success(t('events.eventDetails.responsesTab.responseDeletedSuccessfully') ||
                'Response deleted successfully');
            refetchAttendees();
            onAttendeesChanged?.();
        }
        catch (e) {
            toast.error(e instanceof Error ? e.message : 'Failed to delete');
        }
        setDeleteConfirmationOpen(false);
        setActionTargetId(null);
    };
    // Bulk operations
    const handleBulkDelete = (selectedRows) => {
        if (selectedRows.length === 0) {
            toast.warning('Please select rows to delete');
            return;
        }
        setSelectedRowsForBulkOperation(selectedRows);
        setBulkDeleteConfirmationOpen(true);
    };
    const handleBulkArchive = (selectedRows) => {
        if (selectedRows.length === 0) {
            toast.warning('Please select rows to archive');
            return;
        }
        setSelectedRowsForBulkOperation(selectedRows);
        setBulkArchiveConfirmationOpen(true);
    };
    const handleBulkUnarchive = (selectedRows) => {
        if (selectedRows.length === 0) {
            toast.warning('Please select rows to unarchive');
            return;
        }
        setSelectedRowsForBulkOperation(selectedRows);
        setBulkUnarchiveConfirmationOpen(true);
    };
    const handleBulkDeleteConfirmed = async (rows) => {
        const ids = rows.map(String);
        try {
            await Promise.all(ids.map(async (id) => {
                const data = await dispatch(deleteFamilyFestAttendeeAsync({ attendeeId: id })).unwrap();
                if (!data.isSuccess)
                    throw new Error(data.message || 'Request failed');
            }));
            toast.success(t('events.eventDetails.responsesTab.bulkDeleteSuccess', { count: ids.length }) ||
                `${ids.length} response(s) deleted`);
            refetchAttendees();
            onAttendeesChanged?.();
        }
        catch (e) {
            toast.error(e instanceof Error ? e.message : 'Bulk delete failed');
        }
    };
    const handleBulkArchiveConfirmed = async (rows) => {
        const ids = rows.map(String);
        try {
            await Promise.all(ids.map(async (id) => {
                const data = await dispatch(archiveFamilyFestAttendeeAsync({ attendeeId: id })).unwrap();
                if (!data.isSuccess)
                    throw new Error(data.message || 'Request failed');
            }));
            toast.success(t('events.eventDetails.responsesTab.bulkArchiveSuccess', { count: ids.length }) ||
                `${ids.length} response(s) archived`);
            refetchAttendees();
            onAttendeesChanged?.();
        }
        catch (e) {
            toast.error(e instanceof Error ? e.message : 'Bulk archive failed');
        }
    };
    const handleBulkUnarchiveConfirmed = async (rows) => {
        const ids = rows.map(String);
        try {
            await Promise.all(ids.map(async (id) => {
                const data = await dispatch(unarchiveFamilyFestAttendeeAsync(id)).unwrap();
                if (!data.isSuccess)
                    throw new Error(data.message || 'Request failed');
            }));
            toast.success(t('events.eventDetails.responsesTab.bulkUnarchiveSuccess', { count: ids.length }) ||
                `${ids.length} response(s) unarchived`);
            refetchAttendees();
        }
        catch (e) {
            toast.error(e instanceof Error ? e.message : 'Bulk unarchive failed');
        }
    };
    const fetchReminderEligibleAttendees = React.useCallback(() => {
        if (!eventId)
            return;
        setReminderEligibleLoading(true);
        dispatch(getFamilyFestReminderEligibleAttendeesAsync({
            eventId,
            pageNumber: reminderPaginationModel.page + 1,
            pageSize: reminderPaginationModel.pageSize
        }))
            .unwrap()
            .then((data) => {
            setReminderEligibleRows(data.items);
            setReminderEligibleTotalCount(data.totalCount);
        })
            .catch(() => {
            toast.error(t('events.eventDetails.responsesTab.failedToSendReminderEmail'));
        })
            .finally(() => setReminderEligibleLoading(false));
    }, [dispatch, eventId, reminderPaginationModel, t]);
    useEffect(() => {
        if (!reminderConfirmationOpen)
            return;
        fetchReminderEligibleAttendees();
    }, [reminderConfirmationOpen, fetchReminderEligibleAttendees]);
    const handleOpenReminderDialog = () => {
        setSelectedReminderAttendeeIds([]);
        setReminderPaginationModel({ page: 0, pageSize: 10 });
        setReminderConfirmationOpen(true);
    };
    const handleSendReminderEmailConfirmed = () => {
        const ids = selectedReminderAttendeeIds.map(String);
        if (ids.length === 0)
            return;
        setSendingReminderEmail(true);
        dispatch(sendFamilyFestReminderEmailAsync(ids))
            .unwrap()
            .then((data) => {
            if (!data.isSuccess)
                throw new Error(data.message || 'Request failed');
            const result = data.data;
            if (result && result.failureCount > 0) {
                toast.warning(t('events.eventDetails.responsesTab.reminderEmailPartialSuccess', {
                    success: result.successCount,
                    total: result.totalProcessed
                }));
            }
            else {
                toast.success(t('events.eventDetails.responsesTab.reminderEmailSentSuccessfully', {
                    count: result?.successCount ?? ids.length
                }));
            }
        })
            .catch((e) => {
            toast.error(e instanceof Error
                ? e.message
                : t('events.eventDetails.responsesTab.failedToSendReminderEmail'));
        })
            .finally(() => {
            setSendingReminderEmail(false);
            setReminderConfirmationOpen(false);
            setSelectedReminderAttendeeIds([]);
        });
    };
    const handleExportExcel = async () => {
        if (!eventId) {
            toast.warning(t('common.noEventSelected', 'No event selected.'));
            return;
        }
        try {
            await dispatch(downloadFamilyFestAttendeesExcelAsync(eventId)).unwrap();
            toast.success(t('common.exportSuccess', 'Data exported successfully!'));
        }
        catch (e) {
            toast.error(e instanceof Error
                ? e.message
                : t('common.exportFailed', 'Failed to export data. Please try again.'));
        }
    };
    const handleFamilyInfo = async () => {
        if (!selectedRowId)
            return;
        await openFamilyInfoForRow(selectedRowId);
        handleMenuClose();
    };
    const selectedRow = selectedRowId ? attendees.find((r) => r.id === selectedRowId) : null;
    const hasFamilyMembers = (selectedRow?.memberCount ?? 0) > 0;
    const selectedRowInvitationStatus = selectedRow?.invitationStatus;
    const isSelectedRowInvited = parseFamilyFestInvitationStatus(selectedRowInvitationStatus) === InvitationStatusEnum.Invited;
    const rowMenuActions = [
        ...(selectedTab === 'archive'
            ? [
                {
                    text: t('actions.unarchive', 'UnArchive'),
                    icon: _jsx(UnarchiveOutlined, { style: { width: 20, height: 20 } }),
                    to: () => handleRowUnarchive(selectedRowId),
                    disabled: !canEditEvent || isCollaborator,
                    group: true
                }
            ]
            : [
                {
                    text: t('actions.archive', 'Archive'),
                    icon: _jsx(Archive, { variant: 'Outline', style: { width: 20, height: 20 } }),
                    to: () => handleRowArchive(selectedRowId),
                    disabled: !canEditEvent || isCollaborator,
                    group: true
                },
                ...(canResendInvitation && selectedTab === 'active'
                    ? [
                        {
                            text: t('actions.offboard', 'Offboard'),
                            icon: _jsx(Icon, { icon: 'mdi:account-off-outline', width: 20, height: 20 }),
                            to: () => handleRowOffboard(selectedRowId),
                            group: true
                        }
                    ]
                    : []),
                ...(selectedRow?.attendanceStatus !== FamilyFestAttendanceStatusEnum.NoLongerEmployed
                    ? [
                        {
                            text: t('actions.copyLink', 'Copy link'),
                            icon: _jsx(Icon, { icon: 'eva:link-2-outline', width: 20, height: 20 }),
                            to: () => void handleCopyLink(selectedRowId),
                            group: true
                        }
                    ]
                    : []),
                ...(canResendInvitation && isSelectedRowInvited
                    ? [
                        {
                            text: t('actions.resendInvitation', 'Resend invitation'),
                            icon: _jsx(Icon, { icon: 'eva:email-outline', width: 20, height: 20 }),
                            to: () => void handleResendInvitation(),
                            group: true
                        }
                    ]
                    : []),
            ]),
        ...(hasFamilyMembers
            ? [
                {
                    text: t('familyForm.familyInfo', 'Family info'),
                    icon: _jsx(Icon, { icon: 'mdi:account-group-outline', width: 20, height: 20 }),
                    to: () => void handleFamilyInfo(),
                    group: true
                }
            ]
            : []),
        {
            text: t('actions.delete', 'Delete'),
            icon: _jsx(Trash, { variant: 'Outline', style: { width: 20, height: 20 } }),
            color: theme.palette.error.light,
            to: () => handleDelete(selectedRowId),
            disabled: !canDeleteEvent || isCollaborator,
            group: true
        }
    ];
    const handleResendInvitation = async () => {
        if (!selectedRowId)
            return;
        try {
            const data = await dispatch(resendFamilyFestInvitationEmailAsync(selectedRowId)).unwrap();
            if (!data.isSuccess)
                throw new Error(data.message || 'Could not resend invitation');
            toast.success(t('events.eventDetails.responsesTab.invitationResentSuccessfully', 'Invitation email resent successfully'));
        }
        catch (e) {
            toast.error(e instanceof Error
                ? e.message
                : t('events.eventDetails.responsesTab.failedToResendInvitation', 'Could not resend invitation email'));
        }
        handleMenuClose();
    };
    // Returns the invitation's existing link when it has one (waiting-list invitees keep their sent link).
    const handleCopyLink = async (attendeeId) => {
        if (!attendeeId)
            return;
        try {
            const data = await dispatch(generateFamilyFestInviteLinkAsync(attendeeId)).unwrap();
            if (!data.isSuccess || data.data == null) {
                throw new Error(data.message || 'Could not generate link');
            }
            await navigator.clipboard.writeText(data.data.magicLink);
            toast.success(data.message || t('common.magiclinkCopied'));
        }
        catch (e) {
            toast.error(e instanceof Error ? e.message : 'Could not generate link');
        }
        handleMenuClose();
    };
    const optionsMenuActions = [
        {
            text: t('common.exportToExcel', 'Export to Excel'),
            icon: _jsx(Export, { variant: 'Outline' }),
            group: true,
            to: () => {
                handleOptionsMenuClose();
                void handleExportExcel();
            },
            disabled: false
        }
    ];
    const isWaitlistTab = selectedTab === 'waitlist';
    const showPinnedStatusColumn = selectedTab === 'active' || isWaitlistTab;
    // The waitlist grid's status column uses a different field than the attendee grid.
    const pinnedStatusField = isWaitlistTab ? 'status' : 'invitationStatus';
    const showPinnedActionsColumn = !isTransportManager;
    // Pinned (sticky) columns, ordered from the right edge to the left. To pin another column, add it
    // here with a fixed width (the column definition must use the same width/minWidth/maxWidth).
    const pinnedColumns = React.useMemo(() => {
        const columns = [];
        if (showPinnedActionsColumn) {
            columns.push({
                field: 'actions',
                width: ACTIONS_COLUMN_WIDTH,
                headerSx: { padding: '0 8px !important' },
                cellSx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 8px !important'
                }
            });
        }
        if (showPinnedStatusColumn) {
            columns.push({ field: pinnedStatusField, width: STATUS_COLUMN_WIDTH });
        }
        return columns;
    }, [showPinnedActionsColumn, showPinnedStatusColumn, pinnedStatusField]);
    const pinnedColumnsSx = React.useMemo(() => {
        const pinnedCellShadow = '-4px 0 8px -4px rgba(0, 0, 0, 0.12)';
        const pinnedHeaderShadow = '-4px 0 8px -4px rgba(0, 0, 0, 0.08)';
        const baseBg = theme.palette.common.white;
        const headerBg = getOpaqueColorMix(baseBg, theme.palette.primary.main, 0.08);
        const cellBg = baseBg;
        const rowHoverBg = getOpaqueColorMix(baseBg, theme.palette.primary.main, theme.palette.action.hoverOpacity);
        const rowSelectedBg = getOpaqueColorMix(baseBg, theme.palette.primary.main, theme.palette.action.selectedOpacity);
        const pinnedDivider = `1px solid ${alpha(theme.palette.grey[500], 0.24)}`;
        const notPinnedHeader = pinnedColumns.map((col) => `:not([data-field="${col.field}"])`).join('');
        const stickyPinnedStyles = {
            '& .MuiDataGrid-columnHeaders': {
                zIndex: 3
            },
            [`& .MuiDataGrid-columnHeader${notPinnedHeader}`]: {
                zIndex: 1
            },
            '& .MuiDataGrid-virtualScroller': {
                zIndex: 1
            }
        };
        // Each column sits right of the sum of the widths pinned to its right; only the leftmost
        // pinned column casts a shadow over the scrolling columns.
        let rightOffset = 0;
        pinnedColumns.forEach((col, index) => {
            const isLeftmost = index === pinnedColumns.length - 1;
            const header = `& .MuiDataGrid-columnHeader[data-field="${col.field}"]`;
            const cell = `.MuiDataGrid-cell[data-field="${col.field}"]`;
            stickyPinnedStyles[header] = {
                position: 'sticky',
                right: rightOffset,
                zIndex: 12 - index,
                isolation: 'isolate',
                backgroundColor: `${headerBg} !important`,
                boxShadow: isLeftmost ? pinnedHeaderShadow : 'none',
                borderLeft: pinnedDivider,
                overflow: 'hidden',
                ...col.headerSx,
                ...pinnedColumnSize(col.width),
                ...pinnedHeaderChildrenBg(headerBg)
            };
            stickyPinnedStyles[`& ${cell}`] = {
                position: 'sticky',
                right: rightOffset,
                zIndex: 4,
                isolation: 'isolate',
                backgroundColor: `${cellBg} !important`,
                boxShadow: isLeftmost ? pinnedCellShadow : 'none',
                borderLeft: pinnedDivider,
                overflow: 'hidden',
                ...col.cellSx,
                ...pinnedColumnSize(col.width)
            };
            stickyPinnedStyles[`& .MuiDataGrid-row:hover ${cell}`] = {
                backgroundColor: `${rowHoverBg} !important`
            };
            stickyPinnedStyles[`& .MuiDataGrid-row.Mui-selected ${cell}`] = {
                backgroundColor: `${rowSelectedBg} !important`
            };
            stickyPinnedStyles[`& .MuiDataGrid-row.Mui-selected:hover ${cell}`] = {
                backgroundColor: `${rowSelectedBg} !important`
            };
            rightOffset += col.width;
        });
        return stickyPinnedStyles;
    }, [pinnedColumns, theme]);
    return (_jsxs(_Fragment, { children: [_jsx(Box, { children: eventId && error ? (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }, children: _jsx(Typography, { color: 'error', children: error }) })) : (_jsx(PrimaryGrid, { loading: Boolean(eventId && (selectedTab === 'waitlist' ? waitlistLoading : loading)), disableVirtualization: true, sx: [
                        { height: { xs: 'calc(100dvh - 120px)', sm: 'calc(100dvh - 200px)' } },
                        pinnedColumnsSx
                    ], rows: createFamilyRows(), getRowId: (row) => row.id, showTabs: true, isMultiple: selectedTab === 'waitlist', rowSelectionModel: selectedTab === 'waitlist' ? selectedWaitlistIds : undefined, onSelectionChange: (ids) => {
                        if (selectedTab === 'waitlist')
                            setSelectedWaitlistIds(ids);
                    }, 
                    // Completed waiting-list forms need no link, and invited rows already have one, so their checkbox is disabled.
                    isRowSelectable: (params) => selectedTab !== 'waitlist' ||
                        (params.row?.isRegistrationComplete !== true &&
                            !isWaitlistRowInvited(params.row)), 
                    // archivebutton={true}
                    archivebutton: false, selectedButton: selectedTab, setSelectedButton: setSelectedTab, tabs: [
                        { label: t('common.active', 'Active'), value: 'active' },
                        {
                            label: t('events.eventDetails.responsesTab.notAttending', 'Not Attending'),
                            value: 'notAttending'
                        },
                        ...(isHappinessManager
                            ? [
                                {
                                    label: t('events.eventDetails.responsesTab.waitingList', 'Waiting list'),
                                    value: 'waitlist'
                                }
                            ]
                            : []),
                        { label: t('actions.archive', 'Archive'), value: 'archive' }
                    ], columns: createFamilyColumns(), columnVisibilityModel: { cameFromWaitlist: false }, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, paginationMode: 'server', rowCount: selectedTab === 'waitlist' ? waitlistTotalCount : totalCount, sortingMode: 'server', sortModel: sortModel, onSortModelChange: (model) => {
                        setSortModel(model);
                        setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }, showExportExcelButton: false, filterOpen: gridFilterOpen, onFilterOpenChange: setGridFilterOpen, onApplyFilters: (newFilters) => {
                        setFilters(newFilters);
                    }, filterOptions: selectedTab === 'waitlist'
                        ? {
                            status: [
                                {
                                    value: InvitationStatusEnum.Waitlisted,
                                    label: t('events.eventDetails.responsesTab.waitlistStatusWaiting', 'Waiting')
                                },
                            ]
                        }
                        : {
                            companyName: companies.map((c) => ({ value: c.id, label: c.name })),
                            cameFromWaitlist: [
                                { value: true, label: t('common.yes') || 'Yes' },
                                { value: false, label: t('common.no') || 'No' }
                            ],
                            ...(selectedTab === 'active' && {
                                invitationStatus: [
                                    {
                                        value: InvitationStatusEnum.Invited,
                                        label: t('events.eventDetails.invitationsTab.invited')
                                    },
                                    {
                                        value: InvitationStatusEnum.InProgress,
                                        label: t('events.eventDetails.invitationsTab.inProgress')
                                    },
                                    {
                                        value: InvitationStatusEnum.Registered,
                                        label: t('events.eventDetails.invitationsTab.registered')
                                    }
                                ]
                            })
                        }, resetFiltersKey: resetFiltersKey, headerRightControles: _jsxs(Box, { sx: {
                            display: 'flex',
                            gap: 1.5,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                            ml: { xs: 0, sm: 'auto' }
                        }, children: [_jsx(SearchFilter, { forceDesktopVariant: true, searchValue: searchValue, onSearchChange: setSearchValue, placeholder: t('common.search', 'Search...') ?? 'Search...', onClear: () => setSearchValue(''), showFilterClearButton: Object.keys(filters).length > 0, handleFilterClear: () => {
                                    setFilters({});
                                    setResetFiltersKey((k) => k + 1);
                                }, onFilterClick: () => setGridFilterOpen(true), filterLabel: t('users.filters') ?? 'Filters' }), selectedTab === 'waitlist' && (_jsxs(_Fragment, { children: [_jsx(Tooltip, { title: t('events.eventDetails.responsesTab.waitlistSendLinksTooltip', 'Email the registration link to the selected people so they can complete the form.'), children: _jsx(Box, { component: 'span', display: 'inline-flex', children: _jsx(Button, { startIcon: _jsx(Send2, { variant: 'Outline', size: 20 }), size: isMobile ? 'medium' : 'large', variant: 'outlined', color: 'primary', disabled: selectedWaitlistIds.length === 0 ||
                                                    createFamilyRows().some((r) => selectedWaitlistIds.includes(r.id) && isWaitlistRowInvited(r)) ||
                                                    linksSending ||
                                                    isCollaborator ||
                                                    !canEditEvent, onClick: () => setConfirmLinks({
                                                    ids: createFamilyRows()
                                                        .filter((r) => selectedWaitlistIds.includes(r.id))
                                                        .map((r) => r.invitationId)
                                                }), children: selectedWaitlistIds.length > 0
                                                    ? t('events.eventDetails.responsesTab.waitlistSendLinksCount', {
                                                        count: selectedWaitlistIds.length,
                                                        defaultValue: 'Send registration email ({{count}})'
                                                    })
                                                    : t('events.eventDetails.responsesTab.waitlistSendLinks', 'Send registration email') }) }) }), _jsx(Tooltip, { title: t('events.eventDetails.responsesTab.waitlistSendInviteTooltip', 'Invite people from the waiting list into free slots, in queue order.'), children: _jsx(Box, { component: 'span', display: 'inline-flex', children: _jsx(Button, { startIcon: _jsx(Send2, { variant: 'Outline', size: 20 }), size: isMobile ? 'medium' : 'large', variant: 'outlined', color: 'primary', disabled: isCollaborator || !canEditEvent, onClick: handleOpenInviteDialog, children: t('events.eventDetails.responsesTab.waitlistSendInvite', 'Send invite') }) }) }), _jsx(Tooltip, { title: t('events.eventDetails.responsesTab.waitlistRunAutoInviteTooltip', 'Run the auto-invite job now instead of waiting for its schedule.'), children: _jsx(Box, { component: 'span', display: 'inline-flex', children: _jsx(Button, { startIcon: _jsx(Flash, { variant: 'Outline', size: 20 }), size: isMobile ? 'medium' : 'large', variant: 'outlined', color: 'primary', disabled: isCollaborator || !canEditEvent || autoInviteRunning, onClick: handleOpenAutoInviteConfirm, children: t('events.eventDetails.responsesTab.waitlistRunAutoInvite', 'Run auto-invite job') }) }) })] })), selectedTab !== 'waitlist' && (_jsx(Button, { startIcon: _jsx(Export, { variant: 'Outline' }), size: isMobile ? 'medium' : 'large', variant: 'outlined', disabled: isCollaborator, onClick: handleExportExcel, color: 'primary', children: t('common.exportToExcel', 'Export to Excel') })), selectedTab !== 'archive' && selectedTab !== 'waitlist' && (_jsx(Button, { startIcon: _jsx(AddCircle, { variant: 'Bold', size: 20 }), size: isMobile ? 'medium' : 'large', variant: 'contained', disabled: isCollaborator || !canEditEvent, onClick: () => {
                                    if (!canEditEvent) {
                                        toast.error(t('common.unauthorized') || 'You do not have permission to add attendees');
                                        return;
                                    }
                                    onOpenAddDialog();
                                }, children: t('events.eventDetails.responsesTab.addAttendee') })), Boolean(optionsMenuAnchorEl) && (_jsx(StyledMenu, { anchorEl: optionsMenuAnchorEl, open: Boolean(optionsMenuAnchorEl), onClose: handleOptionsMenuClose, actions: optionsMenuActions }))] }), showInvitationsAccessButton: false, onExportExcelClick: handleExportExcel, showFilters: false, noRowsText: t('events.eventDetails.responsesTab.noResponses'), eventType: EventTypeEnum.Family, disableRowSelectionOnClick: true, pageSizeOptions: [5, 10, 25, 50], onBulkArchive: isCollaborator ? undefined : handleBulkArchive, onBulkUnarchive: isCollaborator ? undefined : handleBulkUnarchive, onBulkDelete: isCollaborator ? undefined : handleBulkDelete })) }), _jsx(SendWaitlistInviteDialog, { open: inviteDialogOpen, onClose: handleCloseInviteDialog, availableCapacity: invitePreview?.availableSpots, expectedPersons: invitePreview?.expectedPersons, isSending: inviteSending, error: inviteError, onClearError: () => setInviteError(null), onSend: handleSendWaitlistInvites }), _jsx(ConvertWaitlistSlotsDialog, { open: convertDialogOpen, onClose: handleCloseConvertDialog, slots: capacitySlots, isSaving: convertSaving, error: convertError, onClearError: () => setConvertError(null), onConvert: handleConvertWaitlistSlots }), _jsx(RunAutoInviteJobDialog, { open: autoInviteConfirmOpen, onClose: () => !autoInviteRunning && setAutoInviteConfirmOpen(false), availableCapacity: autoInvitePreview?.availableSpots ?? (autoInvitePreview ? 0 : undefined), expectedPersons: autoInvitePreview?.expectedPersons, previewFailed: autoInvitePreview === null, lastRunAtUtc: autoInvitePreview ? (autoInvitePreview.lastAutoInviteRunAtUtc ?? null) : undefined, isRunning: autoInviteRunning, error: autoInviteError, onRun: handleRunAutoInvite }), _jsx(ConfirmationDialog, { open: deleteWaitlistRow != null, onClose: () => !deleteWaitlistSaving && setDeleteWaitlistRow(null), title: t('common.deleteConfirmation'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { size: 'large', color: 'inherit', fullWidth: true, variant: 'outlined', disabled: deleteWaitlistSaving, onClick: () => setDeleteWaitlistRow(null), children: t('actions.cancel') }), _jsx(Button, { size: 'large', variant: 'contained', color: 'error', fullWidth: true, disabled: !canDeleteEvent || isCollaborator || deleteWaitlistSaving, onClick: handleConfirmDeleteWaitlistEntry, children: t('actions.deleteNow') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: t('events.eventDetails.responsesTab.waitlistDeleteConfirmText', {
                            name: [deleteWaitlistRow?.firstName, deleteWaitlistRow?.lastName]
                                .filter(Boolean)
                                .join(' '),
                            defaultValue: 'Are you sure you want to remove {{name}} from the waiting list? This action cannot be undone.'
                        }) }) }) }), _jsx(ConfirmationDialog, { open: confirmLinks != null, onClose: () => !linksSending && setConfirmLinks(null), title: t('events.eventDetails.responsesTab.waitlistSendLinksConfirmTitle', 'Send registration email'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { size: 'large', color: 'inherit', fullWidth: true, variant: 'outlined', disabled: linksSending, onClick: () => setConfirmLinks(null), children: t('actions.cancel') }), _jsx(Button, { size: 'large', variant: 'contained', color: 'primary', fullWidth: true, disabled: isCollaborator || !canEditEvent || linksSending, onClick: () => confirmLinks && handleSendWaitlistLinks(confirmLinks.ids), children: t('events.eventDetails.responsesTab.waitlistSendLinksConfirmAction', 'Send') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: confirmLinks?.name
                            ? t('events.eventDetails.responsesTab.waitlistSendLinkConfirmTextName', {
                                name: confirmLinks.name,
                                defaultValue: 'Are you sure you want to send the registration email to {{name}}?'
                            })
                            : t('events.eventDetails.responsesTab.waitlistSendLinksConfirmText', {
                                count: confirmLinks?.ids.length ?? 0,
                                defaultValue: 'Are you sure you want to send the registration email to {{count}} selected registration(s)?'
                            }) }) }) }), _jsx(MoveWaitlistDialog, { open: moveDialogRow != null, onClose: () => !moveSaving && setMoveDialogRow(null), row: moveDialogRow, waitlistedCount: waitlistedCount, isSaving: moveSaving, onMove: (target) => moveDialogRow && handleMoveWaitlist(moveDialogRow, target) }), editRegistrationRow && (_jsx(EditFamilyResponseDialog, { open: editRegistrationRow != null, onClose: () => setEditRegistrationRow(null), userId: editRegistrationRow.id, userName: [editRegistrationRow.firstName, editRegistrationRow.lastName].filter(Boolean).join(' '), eventId: eventId, paginationModel: paginationModel, onRefreshAttendees: () => refetchAttendees(), isWaitingList: selectedTab === 'waitlist' })), Boolean(waitlistMenuAnchorEl) && (_jsx(StyledMenu, { anchorEl: waitlistMenuAnchorEl, open: Boolean(waitlistMenuAnchorEl), onClose: closeWaitlistMenu, actions: waitlistRowMenuActions })), Boolean(menuAnchorEl) && (_jsx(StyledMenu, { anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl), onClose: handleMenuClose, actions: rowMenuActions })), _jsx(ConfirmationDialog, { open: archiveConfirmationOpen, onClose: () => {
                    setArchiveConfirmationOpen(false);
                    setActionTargetId(null);
                }, title: selectedTab === 'archive'
                    ? t('common.unarchiveConfirmation')
                    : t('common.archiveConfirmation'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, size: 'large', variant: 'outlined', color: 'inherit', onClick: () => {
                                setArchiveConfirmationOpen(false);
                                setActionTargetId(null);
                            }, children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, size: 'large', variant: 'contained', color: 'inherit', onClick: selectedTab === 'archive' ? handleConfirmUnArchive : handleConfirmArchive, disabled: !canEditEvent || isCollaborator, children: selectedTab === 'archive' ? t('actions.unarchive') : t('actions.archive') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: selectedTab === 'archive'
                            ? t('events.eventDetails.responsesTab.confirmUnArchiveText') ||
                                'Are you sure you want to unarchive this response?'
                            : t('events.eventDetails.responsesTab.confirmArchiveText') }) }) }), _jsx(ConfirmationDialog, { open: offboardConfirmationOpen, onClose: () => {
                    setOffboardConfirmationOpen(false);
                    setActionTargetId(null);
                    setOffboardTargetName('');
                }, title: t('common.offboardConfirmation', 'Offboard confirmation'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, size: 'large', variant: 'outlined', color: 'inherit', onClick: () => {
                                setOffboardConfirmationOpen(false);
                                setActionTargetId(null);
                                setOffboardTargetName('');
                            }, children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, size: 'large', variant: 'contained', color: 'inherit', onClick: handleConfirmOffboard, children: t('actions.offboard', 'Offboard') })] }), children: _jsxs(Box, { padding: 3, children: [_jsx(Typography, { variant: 'body1', children: t('events.eventDetails.responsesTab.confirmOffboardText', {
                                name: offboardTargetName,
                                defaultValue: "Are you sure you want to offboard '{{name}}'?"
                            }) }), _jsx(Typography, { variant: 'body2', sx: { mt: 1, color: theme.palette.text.secondary }, children: t('events.eventDetails.responsesTab.confirmOffboardSubtext', 'This will mark them as not attending and clear their travel and accommodation data.') })] }) }), _jsx(ConfirmationDialog, { open: deleteConfirmationOpen, onClose: () => {
                    setDeleteConfirmationOpen(false);
                    setActionTargetId(null);
                }, title: t('common.deleteConfirmation'), actions: _jsxs(_Fragment, { children: [_jsx(Button, { size: 'large', color: 'inherit', fullWidth: true, variant: 'outlined', onClick: () => {
                                setDeleteConfirmationOpen(false);
                                setActionTargetId(null);
                            }, children: t('actions.cancel') }), _jsx(Button, { size: 'large', variant: 'contained', color: 'error', fullWidth: true, disabled: !canDeleteEvent || isCollaborator, onClick: handleConfirmDelete, children: t('actions.deleteNow') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: t('events.eventDetails.responsesTab.confirmDeleteText') ||
                            'Are you sure you want to delete this response? This action cannot be undone.' }) }) }), _jsx(ConfirmationDialog, { open: bulkDeleteConfirmationOpen, onClose: () => {
                    setBulkDeleteConfirmationOpen(false);
                    setSelectedRowsForBulkOperation([]);
                }, title: t('events.eventDetails.responsesTab.bulkDeleteConfirmationTitle') || 'Confirm Bulk Delete', actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', size: 'large', color: 'inherit', onClick: () => {
                                setBulkDeleteConfirmationOpen(false);
                                setSelectedRowsForBulkOperation([]);
                            }, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'inherit', size: 'large', fullWidth: true, onClick: () => {
                                handleBulkDeleteConfirmed(selectedRowsForBulkOperation);
                                setBulkDeleteConfirmationOpen(false);
                                setSelectedRowsForBulkOperation([]);
                            }, children: t('actions.deleteNow') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: t('events.eventDetails.responsesTab.bulkDeleteConfirmationText', {
                            count: selectedRowsForBulkOperation.length
                        }) ||
                            `Are you sure you want to delete ${selectedRowsForBulkOperation.length} selected response(s)? This action cannot be undone.` }) }) }), _jsx(ConfirmationDialog, { open: bulkArchiveConfirmationOpen, onClose: () => {
                    setBulkArchiveConfirmationOpen(false);
                    setSelectedRowsForBulkOperation([]);
                }, title: t('events.eventDetails.responsesTab.bulkArchiveConfirmationTitle') ||
                    'Confirm Bulk Archive', actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', size: 'large', color: 'inherit', onClick: () => {
                                setBulkArchiveConfirmationOpen(false);
                                setSelectedRowsForBulkOperation([]);
                            }, children: t('actions.cancel') }), _jsx(Button, { size: 'large', fullWidth: true, variant: 'contained', color: 'inherit', disabled: !canEditEvent || isCollaborator, onClick: () => {
                                handleBulkArchiveConfirmed(selectedRowsForBulkOperation);
                                setBulkArchiveConfirmationOpen(false);
                                setSelectedRowsForBulkOperation([]);
                            }, children: t('actions.archive') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: t('events.eventDetails.responsesTab.bulkArchiveConfirmationText', {
                            count: selectedRowsForBulkOperation.length
                        }) ||
                            `Are you sure you want to archive ${selectedRowsForBulkOperation.length} selected response(s)?` }) }) }), _jsxs(CustomDialog, { open: reminderConfirmationOpen, onClose: () => {
                    if (sendingReminderEmail)
                        return;
                    setReminderConfirmationOpen(false);
                }, titleWithIcon: t('events.eventDetails.responsesTab.sendReminderEmailTitle'), titleIcon: '/assets/icons/star_header.png', maxWidth: 'md', backgroundVariant: 'white', closeIcon: true, actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, size: 'large', variant: 'outlined', color: 'inherit', disabled: sendingReminderEmail, onClick: () => setReminderConfirmationOpen(false), children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, size: 'large', variant: 'contained', color: 'primary', disabled: sendingReminderEmail || selectedReminderAttendeeIds.length === 0, onClick: handleSendReminderEmailConfirmed, children: sendingReminderEmail ? (_jsx(CircularProgress, { size: 20, color: 'inherit' })) : (t('events.eventDetails.responsesTab.sendReminderEmail')) })] }), children: [_jsx(Box, { sx: { px: 3, pb: 1 }, children: _jsx(Typography, { variant: 'body2', color: 'text.secondary', textAlign: 'center', children: t('events.eventDetails.responsesTab.sendReminderEmailSubtitle', {
                                count: selectedReminderAttendeeIds.length
                            }) }) }), _jsx(Box, { sx: { px: 3, pb: 3 }, children: _jsx(PrimaryGrid, { loading: reminderEligibleLoading, columns: reminderPreviewColumns, rows: reminderEligibleRows, getRowId: (row) => row.id, showHeaderControls: false, showNewHeaderControls: false, maxHeight: '300px', noRowsText: t('events.eventDetails.responsesTab.noResponses'), isMultiple: true, disableRowSelectionOnClick: true, rowSelectionModel: selectedReminderAttendeeIds, onSelectionChange: setSelectedReminderAttendeeIds, paginationMode: 'server', paginationModel: reminderPaginationModel, onPaginationModelChange: setReminderPaginationModel, rowCount: reminderEligibleTotalCount, pageSizeOptions: [5, 10, 25, 50] }) })] }), _jsx(FamilyInfoDialog, { open: familyInfoOpen, onClose: () => setFamilyInfoOpen(false), persons: familyInfoPersons }), _jsx(ConfirmationDialog, { open: bulkUnarchiveConfirmationOpen, onClose: () => {
                    setBulkUnarchiveConfirmationOpen(false);
                    setSelectedRowsForBulkOperation([]);
                }, title: t('events.eventDetails.responsesTab.bulkUnarchiveConfirmationTitle') ||
                    'Confirm Bulk Unarchive', actions: _jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', size: 'large', color: 'inherit', onClick: () => {
                                setBulkUnarchiveConfirmationOpen(false);
                                setSelectedRowsForBulkOperation([]);
                            }, children: t('actions.cancel') }), _jsx(Button, { size: 'large', variant: 'contained', color: 'inherit', fullWidth: true, disabled: !canEditEvent || isCollaborator, onClick: () => {
                                handleBulkUnarchiveConfirmed(selectedRowsForBulkOperation);
                                setBulkUnarchiveConfirmationOpen(false);
                                setSelectedRowsForBulkOperation([]);
                            }, children: t('actions.unarchive') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: t('events.eventDetails.responsesTab.bulkUnarchiveConfirmationText', {
                            count: selectedRowsForBulkOperation.length
                        }) ||
                            `Are you sure you want to unarchive ${selectedRowsForBulkOperation.length} selected response(s)?` }) }) })] }));
};
export default FamilyResponsesGrid;
